/**
 * V2 follow task — estimated hourly distribution for UI preview only.
 * Uses a humanized diurnal shape (quieter night, busier daytime) plus
 * deterministic per-hour jitter so the chart looks natural, not flat.
 * Does not drive backend scheduling.
 */

export function computeV2HourlyDistribution(accountCount, dailyMin, dailyMax, activeHoursPerDay = 12) {
  const n = Math.max(0, accountCount);
  const lo = Math.max(1, dailyMin);
  const hi = Math.max(lo, dailyMax);
  const totalMin = n * lo;
  const totalMax = n * hi;
  const focus = Math.max(0.25, Math.min(1, activeHoursPerDay / 24));

  const raw = Array.from({ length: 24 }, (_, h) => {
    let base;
    if (h >= 8 && h <= 19) {
      const x = (h - 13.5) / 5;
      base = 0.5 + 0.5 * Math.exp(-x * x * 0.45);
    } else if (h >= 0 && h <= 7) {
      base = 0.1 + 0.05 * Math.sin((h + 3) * 0.55);
    } else {
      base = 0.22 + 0.08 * Math.sin((h - 20) * 0.9);
    }

    const dayBoost = 0.65 + 0.35 * focus;
    const nightDamp = 1.35 - 0.35 * focus;
    if (h >= 8 && h <= 19) base *= dayBoost;
    else base *= nightDamp;

    const jitter = 0.9 + ((((h * 7919) ^ (h * 31 + 17)) % 21) + 10) / 100;
    return Math.max(0.02, base * jitter);
  });

  const sum = raw.reduce((a, b) => a + b, 0);
  const weights = raw.map((w) => w / sum);

  const hours = weights.map((weight, hour) => {
    const estMin = Math.round(totalMin * weight);
    const estMax = Math.round(totalMax * weight);
    const mid = (estMin + estMax) / 2;
    return {
      hour,
      label: String(hour).padStart(2, '0'),
      weight,
      estMin,
      estMax,
      mid,
      band: 'night',
    };
  });

  hours.forEach((row) => {
    if (row.hour >= 8 && row.hour <= 19) row.band = 'day';
    else if (row.hour >= 20) row.band = 'evening';
    else row.band = 'night';
  });

  const peakMid = Math.max(...hours.map((h) => h.mid), 1);
  hours.forEach((row) => {
    row.height01 = row.mid / peakMid;
  });

  return {
    hours,
    totalMin,
    totalMax,
    peakMid,
  };
}

/** Typical Camoufox follow duration (seconds) for throughput estimates. */
export const SEC_PER_FOLLOW_LOW = 10;
export const SEC_PER_FOLLOW_HIGH = 11;
/** Assumed concurrent browser slots per login-worker replica (common default). */
export const SLOTS_PER_REPLICA_TYPICAL = 2;

/**
 * Peak-hour browser slot / replica demand from hourly estimates (preview only).
 * Uses max hourly estMax across 24h vs follows/hour per slot at 10–11s per follow.
 */
export function computeBrowserFleetSizing(hours, options = {}) {
  const secLo = options.secPerFollowLow ?? SEC_PER_FOLLOW_LOW;
  const secHi = options.secPerFollowHigh ?? SEC_PER_FOLLOW_HIGH;
  const slotsPerReplica = options.slotsPerReplica ?? SLOTS_PER_REPLICA_TYPICAL;

  if (!hours?.length) return null;

  let peakIdx = 0;
  let peakEstMax = 0;
  for (let i = 0; i < hours.length; i++) {
    if (hours[i].estMax > peakEstMax) {
      peakEstMax = hours[i].estMax;
      peakIdx = i;
    }
  }
  if (peakEstMax <= 0) return null;

  const peakHour = hours[peakIdx];
  const fphOptimistic = 3600 / secLo;
  const fphConservative = 3600 / secHi;

  const slotsNeededMin = Math.ceil(peakEstMax / fphOptimistic);
  const slotsNeededMax = Math.ceil(peakEstMax / fphConservative);

  const replicasNeededMin = Math.ceil(slotsNeededMin / slotsPerReplica);
  const replicasNeededMax = Math.ceil(slotsNeededMax / slotsPerReplica);

  return {
    peakHourLabel: peakHour.label,
    peakEstMin: peakHour.estMin,
    peakEstMax,
    secPerFollowRange: `${secLo}–${secHi}`,
    followsPerSlotPerHour: {
      min: Math.round(fphConservative),
      max: Math.round(fphOptimistic),
    },
    slotsNeeded: { min: slotsNeededMin, max: slotsNeededMax },
    replicasNeeded: { min: replicasNeededMin, max: replicasNeededMax },
    slotsPerReplica,
  };
}
